
import { 
  CheckSquare, 
  Calendar, 
  FileText, 
  Users, 
  Clock, 
  BarChart3, 
  MessageSquare, 
  Zap,
  Target,
  Briefcase,
  Settings,
  TrendingUp,
  Layers,
  Video,
  Brain,
  BookOpen,
  Gamepad2,
  Workflow,
  Eye,
  FolderOpen,
  Heart,
  Focus,
  Activity,
  BarChart2,
  Award,
  Timer
} from 'lucide-react';
import type { Product } from './types';

export const productivityProducts: Product[] = [
  {
    id: 'project-management-suite',
    title: 'Project Management Suite',
    subtitle: 'Complete project management platform',
    description: 'Comprehensive project management with task tracking, team collaboration, resource planning, and progress monitoring.',
    icon: CheckSquare,
    image: 'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#project-management', '#enterprise', '#teams', '#consulting', '#agencies'],
    features: [
      'Task Management & Assignment',
      'Gantt Charts & Timeline Planning',
      'Team Collaboration & Communication',
      'Resource Planning & Allocation',
      'Time Tracking & Reporting',
      'Budget Management & Cost Tracking',
      'File Sharing & Document Management',
      'Progress Monitoring & Analytics',
      'Custom Workflows & Automation',
      'Integration with Popular Tools'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'team-collaboration-platform',
    title: 'Team Collaboration Platform',
    subtitle: 'Unified team communication hub',
    description: 'Complete team collaboration platform with messaging, video conferencing, file sharing, and workflow automation.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#remote-teams', '#enterprise', '#startups', '#freelancers', '#distributed-teams'],
    features: [
      'Team Messaging & Chat Channels',
      'Video Conferencing & Screen Sharing',
      'File Sharing & Cloud Storage',
      'Workflow Automation & Integration',
      'Calendar Integration & Scheduling',
      'Task Management & To-Do Lists',
      'Knowledge Base & Documentation',
      'Mobile App & Cross-Platform Access',
      'Security & Permission Management',
      'Analytics & Usage Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'time-tracking-analytics',
    title: 'Time Tracking & Analytics',
    subtitle: 'Advanced time management platform',
    description: 'Comprehensive time tracking with project analytics, productivity insights, and automated reporting.',
    icon: Clock,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#freelancers', '#agencies', '#consulting', '#professional-services', '#remote-work'],
    features: [
      'Automated Time Tracking & Recording',
      'Project & Client Time Management',
      'Productivity Analytics & Insights',
      'Timesheet Management & Approval',
      'Billing & Invoice Integration',
      'Team Performance Monitoring',
      'Custom Reports & Analytics',
      'Mobile Time Tracking App',
      'Integration with Project Management Tools',
      'Real-Time Dashboard & Notifications'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'document-management-system',
    title: 'Document Management System',
    subtitle: 'Enterprise document platform',
    description: 'Complete document management with version control, collaboration, workflow automation, and security features.',
    icon: FileText,
    image: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#enterprise', '#legal', '#healthcare', '#finance', '#document-heavy-industries'],
    features: [
      'Document Storage & Organization',
      'Version Control & History Tracking',
      'Collaboration & Real-Time Editing',
      'Workflow Automation & Approval',
      'Security & Access Control',
      'Search & Discovery Tools',
      'Integration with Office Suites',
      'Audit Trail & Compliance',
      'Mobile Document Access',
      'Backup & Recovery Features'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'miro-collaborative-whiteboard',
    title: 'Miro',
    subtitle: 'Collaborative whiteboard platform',
    description: 'Visual collaboration platform used by 60 million users for agile workflows, diagrams, workshops, and team brainstorming.',
    icon: Layers,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#visual-collaboration', '#design-thinking', '#agile', '#workshops', '#remote-teams'],
    features: [
      'Infinite Canvas & Visual Collaboration',
      'Pre-built Templates & Frameworks',
      'Real-Time Collaboration & Cursors',
      'Sticky Notes & Digital Whiteboarding',
      'Mind Mapping & Diagramming Tools',
      'Integration with Popular Tools',
      'Workshop & Meeting Facilitation',
      'Project Planning & Roadmapping',
      'Design Thinking & Innovation Tools',
      'Mobile App & Cross-Platform Access'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-600'
  },
  {
    id: 'interactive-workspace',
    title: 'Interactive Workspace Platform',
    subtitle: 'Unified virtual collaboration environment',
    description: 'Combines video conferencing, document collaboration, and project management in a unified virtual workspace.',
    icon: Video,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#virtual-workspace', '#remote-collaboration', '#hybrid-work', '#video-conferencing', '#document-collaboration'],
    features: [
      'HD Video Conferencing & Screen Sharing',
      'Real-Time Document Collaboration',
      'Virtual Whiteboard & Annotation Tools',
      'Integrated Project Management',
      'File Sharing & Cloud Storage',
      'Breakout Rooms & Team Spaces',
      'Recording & Playback Features',
      'Calendar Integration & Scheduling',
      'Mobile Apps & Cross-Platform Support',
      'Advanced Security & Encryption'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'meeting-intelligence',
    title: 'Meeting Intelligence Platform',
    subtitle: 'AI-powered meeting optimization',
    description: 'Records, transcribes, and analyses meetings to extract action items and insights automatically using AI with UK data protection compliance.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#meeting-intelligence', '#ai-transcription', '#action-items', '#meeting-analytics', '#productivity'],
    features: [
      'AI-Powered Meeting Transcription',
      'Automatic Action Item Extraction',
      'Meeting Summary & Key Insights',
      'Sentiment Analysis & Speaker Analytics',
      'Integration with Calendar & CRM',
      'Searchable Meeting Archive',
      'Follow-up Task Assignment',
      'Meeting Performance Analytics',
      'Multi-Language Support',
      'Privacy & Compliance Controls'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'knowledge-management',
    title: 'Knowledge Management System',
    subtitle: 'Organizational knowledge platform',
    description: 'Captures and organizes institutional knowledge to make it easily accessible and searchable for employees.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#knowledge-management', '#documentation', '#information-systems', '#organizational-learning', '#content-management'],
    features: [
      'Centralized Knowledge Repository',
      'Advanced Search & Discovery',
      'Content Creation & Editing Tools',
      'Version Control & History Tracking',
      'Collaboration & Review Workflows',
      'AI-Powered Content Suggestions',
      'Analytics & Usage Insights',
      'Integration with Business Tools',
      'Access Control & Permissions',
      'Mobile App & Offline Access'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'virtual-team-building',
    title: 'Virtual Team Building Platform',
    subtitle: 'Remote team engagement solution',
    description: 'Facilitates remote team bonding activities and social connections for distributed teams and hybrid workforces.',
    icon: Gamepad2,
    image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#team-building', '#remote-culture', '#employee-engagement', '#virtual-events', '#team-bonding'],
    features: [
      'Virtual Team Building Activities',
      'Interactive Games & Challenges',
      'Social Connection Tools',
      'Event Planning & Scheduling',
      'Team Performance Tracking',
      'Custom Activity Creation',
      'Integration with Video Platforms',
      'Employee Recognition Features',
      'Cultural Assessment Tools',
      'Analytics & Engagement Metrics'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-pink-500 to-purple-600'
  },
  {
    id: 'workflow-automation-builder',
    title: 'Workflow Automation Builder',
    subtitle: 'No-code process automation',
    description: 'No-code platform for creating custom workflows that connect different applications and automate business processes.',
    icon: Workflow,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#workflow-automation', '#no-code', '#process-automation', '#integration', '#business-process'],
    features: [
      'Drag & Drop Workflow Builder',
      'Pre-built Automation Templates',
      'Multi-App Integration & Connectors',
      'Conditional Logic & Decision Trees',
      'Real-Time Process Monitoring',
      'Error Handling & Retry Logic',
      'Scheduled & Triggered Workflows',
      'Custom Field Mapping',
      'API Integration Capabilities',
      'Performance Analytics & Insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-600'
  },
  {
    id: 'decision-management',
    title: 'Decision Management Platform',
    subtitle: 'Structured decision-making system',
    description: 'Structures decision-making processes with frameworks for analysis, documentation, and implementation tracking.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#decision-management', '#strategic-planning', '#governance', '#risk-management', '#business-analysis'],
    features: [
      'Decision Framework Templates',
      'Stakeholder Collaboration Tools',
      'Risk Assessment & Analysis',
      'Impact & Outcome Tracking',
      'Decision Documentation & History',
      'Approval Workflows & Governance',
      'Data-Driven Decision Support',
      'Implementation Tracking',
      'Custom Criteria & Scoring',
      'Decision Analytics & Reporting'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'executive-visibility',
    title: 'Executive Visibility Platform',
    subtitle: 'Leadership dashboard & insights',
    description: 'Provides dashboards and reports to keep leadership informed about organizational performance and key initiatives.',
    icon: Eye,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#executive-dashboard', '#leadership-insights', '#organizational-performance', '#kpi-tracking', '#business-intelligence'],
    features: [
      'Executive Dashboard & KPI Tracking',
      'Real-Time Performance Metrics',
      'Strategic Initiative Monitoring',
      'Custom Report Generation',
      'Cross-Department Analytics',
      'Mobile Executive App',
      'Automated Alert & Notification System',
      'Data Visualization & Charts',
      'Integration with Business Systems',
      'Scheduled Report Distribution'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-teal-500 to-cyan-600'
  },
  {
    id: 'digital-asset-management',
    title: 'Digital Asset Management',
    subtitle: 'Enterprise content organization',
    description: 'Centralizes storage, organization, and distribution of digital assets with rights management and analytics.',
    icon: FolderOpen,
    image: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#digital-assets', '#content-management', '#brand-management', '#creative-operations', '#media-management'],
    features: [
      'Centralized Asset Repository',
      'Advanced Search & Metadata',
      'Brand Guidelines & Compliance',
      'Rights Management & Licensing',
      'Automated Workflow & Approval',
      'Version Control & History',
      'Integration with Creative Tools',
      'Usage Analytics & Insights',
      'Custom Branding & Portals',
      'API & Integration Capabilities'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-violet-500 to-purple-600'
  },
  {
    id: 'employee-experience',
    title: 'Employee Experience Platform',
    subtitle: 'Workforce engagement analytics',
    description: 'Measures and improves employee engagement through feedback tools, pulse surveys, and experience analytics.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#employee-experience', '#engagement', '#pulse-surveys', '#feedback', '#workplace-analytics'],
    features: [
      'Employee Pulse Surveys & Feedback',
      'Experience Journey Mapping',
      'Real-Time Sentiment Analysis',
      'Anonymous Feedback Channels',
      'Manager Insights & Coaching',
      'Action Planning & Follow-up',
      'Benchmark & Trend Analysis',
      'Integration with HR Systems',
      'Custom Survey Builder',
      'Advanced Analytics & Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-rose-500 to-pink-600'
  },
  {
    id: 'smart-calendar-coordination',
    title: 'Smart Calendar Coordination',
    subtitle: 'AI-powered scheduling optimisation',
    description: 'Uses AI to optimise team scheduling, find optimal meeting times, and coordinate across multiple calendar systems for UK work patterns.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1506485338023-6ce5f36692df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#calendar-management', '#scheduling', '#ai-optimization', '#meeting-coordination', '#productivity'],
    features: [
      'AI-Powered Meeting Scheduling',
      'Cross-Platform Calendar Integration',
      'Optimal Time Slot Suggestions',
      'Automatic Time Zone Conversion',
      'Meeting Room & Resource Booking',
      'Conflict Resolution & Rescheduling',
      'Preference Learning & Adaptation',
      'Travel Time Considerations',
      'Buffer Time Management',
      'Analytics & Scheduling Insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'enterprise-productivity-analytics-suite',
    title: 'Enterprise Productivity & Focus Analytics Suite',
    subtitle: 'Advanced workforce productivity platform',
    description: 'Enterprise-grade productivity management with AI-powered analytics, team optimization insights, advanced focus tracking, and workplace performance optimization for distributed teams.',
    icon: Activity,
    image: 'https://images.unsplash.com/photo-1494059980473-813e73ee784b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#enterprise-productivity', '#workforce-analytics', '#performance-optimization', '#team-insights', '#workplace-intelligence'],
    features: [
      'AI-powered productivity analytics',
      'Advanced focus time tracking',
      'Team performance optimization',
      'Distraction pattern analysis',
      'Work-life balance monitoring',
      'Meeting load optimization',
      'Deep work scheduling',
      'Burnout risk detection',
      'Collaboration quality metrics',
      'Manager insight dashboards',
      'Individual productivity coaching',
      'Team wellness indicators'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'personal-productivity-suite',
    title: 'Personal Productivity Suite',
    subtitle: 'Individual performance optimization',
    description: 'Personal productivity tool with focus timers, habit tracking, goal management, and productivity analytics for individual professionals.',
    icon: Focus,
    image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#personal-productivity', '#focus', '#habit-tracking', '#goals', '#individual'],
    features: [
      'Pomodoro Focus Timer',
      'Daily & Weekly Goal Setting',
      'Habit Tracking & Streaks',
      'Time Blocking & Planning',
      'Productivity Score & Insights',
      'Distraction Blocking',
      'Energy Level Tracking',
      'Personal Dashboard',
      'Progress Visualization',
      'Mobile Sync & Notifications'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'okr-goal-management',
    title: 'OKR & Goal Management Platform',
    subtitle: 'Objectives and key results tracking',
    description: 'Comprehensive OKR platform with goal setting, progress tracking, team alignment, and performance analytics.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#okr', '#goals', '#performance', '#alignment', '#strategy'],
    features: [
      'OKR Framework Implementation',
      'Goal Cascading & Alignment',
      'Progress Tracking & Check-ins',
      'Team & Department Goals',
      'Key Results Measurement',
      'Performance Reviews Integration',
      'Dashboard & Visualization',
      'Coaching & Feedback Tools',
      'Historical Analysis & Trends',
      'Integration with HR Systems'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'team-analytics-dashboard',
    title: 'Team Analytics Dashboard',
    subtitle: 'Team performance insights',
    description: 'Advanced team analytics with performance metrics, collaboration patterns, workload distribution, and team health indicators.',
    icon: BarChart2,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#team-analytics', '#performance', '#collaboration', '#management', '#insights'],
    features: [
      'Team Performance Metrics',
      'Collaboration Pattern Analysis',
      'Workload Distribution View',
      'Team Health Indicators',
      'Skill Gap Identification',
      'Engagement Tracking',
      'Burnout Risk Assessment',
      'Manager Recommendations',
      'Trend Analysis & Forecasting',
      'Custom Reporting'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-orange-500 to-amber-600'
  },
  {
    id: 'recognition-rewards-platform',
    title: 'Recognition & Rewards Platform',
    subtitle: 'Employee appreciation system',
    description: 'Complete employee recognition platform with peer-to-peer recognition, rewards management, and appreciation analytics.',
    icon: Award,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#recognition', '#rewards', '#employee-engagement', '#culture', '#appreciation'],
    features: [
      'Peer-to-Peer Recognition',
      'Manager Recognition Tools',
      'Points & Rewards System',
      'Celebration Automation',
      'Recognition Wall & Feed',
      'Rewards Marketplace',
      'Budget Management',
      'Analytics & Reporting',
      'Integration with HR Systems',
      'Mobile Recognition App'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-yellow-500 to-orange-600'
  },
  {
    id: 'time-management-analytics',
    title: 'Time Management Analytics Platform',
    subtitle: 'Advanced time intelligence',
    description: 'Comprehensive time management analytics with automatic time tracking, project allocation, and productivity optimization insights.',
    icon: Timer,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Productivity & Collaboration',
    status: 'Available',
    businessTypes: ['#time-management', '#analytics', '#productivity', '#tracking', '#optimization'],
    features: [
      'Automatic Time Capture',
      'Project Time Allocation',
      'Productivity Patterns Analysis',
      'Meeting Time Analytics',
      'Focus Time Optimization',
      'Client & Project Reporting',
      'Team Time Insights',
      'Integration with Tools',
      'Mobile Time Tracking',
      'AI-Powered Recommendations'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-indigo-500 to-blue-600'
  }
];
